import React, { useState, useEffect } from 'react';
import './Configurator.scss';
import CanvasContainer from './CanvasContainer.js';
import StyleInfo from './StyleInfo.js';
import { validateStyle, handleStyleUpdate } from './ValidateConfigurator.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

function Configurator (props) {
  const [updateToggle, setUpdateToggle] = useState(true)
  const [rerender, setRerender] = useState(true)
  const [caseSettings, setCaseSettings] = useState({
    caseName: 'K',
    valid: "",
    cardSlots: 7,
    analogMax: 4,
    ioMax: 6,
    commMax: 1,
    hardwareStyle: ["K", "N0", "N0", "N0", "N0", "N0", "N0", "1", "N", "0", "A", "00", "N"],
    styleCode: "BE1-FLEX ******",
    models: ["","","","","","",""],
    analogBoards: [
      "9579209100","T3",0,
      "9579209102","M0",0,  
      "9579210100","X6",0,
      "9579210101","L2",0,
      "9579210102","L6",0,
      "9579210103","A9",0,
      "9579209101","X9",0
    ],
    ioBoards: [
      "9579212100","W9",0,
      "9579216101","N5",0,
      "9579216101","U4",0,
      "9579227100","C5",0,
      "9579216100","A2",0
    ],
    commBoards: [
      "9579207100","E5",0,
      "9579207101","W2",0,
      "9579208100","P7",0,
      "9579208101","G3",0,
      "9579208102","H8",0,
      "9579208103","H7",0
    ],
    powerBoards: [
      "9579206100","3",
      "9579206101","1",
      "9579206102","2"
    ]
  })

  const modalToggler = (view) => {
    props.modalToggler(view, "")
  }

  //Toggles the config vs model button on mobile and the update 3D model button on web
  const toggleButton = () => {
    if (updateToggle === true) {
      setUpdateToggle(false)
    } else {
      setUpdateToggle(true)
    }
  }

  //Toggles if the canvas should rerender or not
  const toggleRender = () => {
    if(rerender === true) {
      setRerender(false)
      if (props.windowSize === "Web") {
        setUpdateToggle(false)
      }
    } else {
      setRerender(true)
      //Make sure 
      if (props.windowSize === "Web") {
        setUpdateToggle(true)
      }
    }
  }

  useEffect(() => {
    function validate() {
      //Check if configuration is valid
      if (validateStyle(caseSettings) === true) {
        //If web view, toggle the update 3D model button to active
        if (props.windowSize === "Web") {
          setUpdateToggle(true);
        }
        setCaseSettings(handleStyleUpdate(caseSettings));
      } else {
        setCaseSettings({...caseSettings, valid: ""});
        //If web view, the style is not valid, toggle button to inactive
        if (props.windowSize === "Web") {
          setUpdateToggle(false);
        }
      }
    }

    document.querySelectorAll("#style-configurator input").forEach(i => {
      i.addEventListener('input', validate);
    })

    return () => {
      document.querySelectorAll("#style-configurator input").forEach(i => {
        i.removeEventListener('input', validate);
      })
    }
  })

  //If web view, show model and configurator. Otherwise, show whichever one is currently active.
  let visibleModel = "active"
  let visibleConfig = "active"
  if (props.windowSize !== "Web") {
    if (updateToggle === true) {
      visibleConfig = "" 
    } else {
      visibleModel = ""
    }
  }

  let canvas = <CanvasContainer 
    windowSize={props.windowSize}
    visible={visibleModel}
    models={caseSettings.models}
    rerender={rerender}
    rendered={toggleRender}
  /> 

  return (
    <div className={'Configurator ' + props.windowSize}>
      <div className="config-container">
        <div className="config-header">
          <h2>BE1-FLEX Configurator</h2>
          <p>
            This configurator allows you to build a custom FLEX relay that is unique to your application. Once you build a valid style, you can rotate and zoom in on your custom FLEX.
          </p>
        </div>
        {canvas}
        <StyleInfo 
          caseSettings={caseSettings}
          windowSize={props.windowSize}
          toggle={updateToggle}
          toggleButton={toggleButton}
          rerender={toggleRender}
          modalToggler={modalToggler}
        />
        <div id="style-configurator" className={visibleConfig}>
          <h2>Style Configurator</h2>
          
          <div id="case">
            <h3>Case</h3>
            <p>
              <input type="radio" id="k" name="case" value="K" defaultChecked="true" />
              <label>K) Medium Vertical</label>
            </p>
          </div>

          <div id="caseNotes">
            <h3>Case Notes</h3>
            <label>
              Up to 7 total boards can be selected below: 
              <ol>
                <li>Power Supply board may only be assigned to slot 1</li>
                <li>Analog boards may be assigned to slots 4-7 only</li>
                <li>Communications board may only be assigned to slot 2</li>  
                <li>I/O boards may be assigned to open slots 2-7</li>
              </ol>
            </label>
          </div>

          <div id="analogBoards">
            <h3>Analog Cards (Maximum of 4, slots 4-7)</h3>
            <p>
              <input type="number" id="T3" name="analogBoards" min="0" max="4" defaultValue="0"/>
              <label>T3) 4 channel voltage (300 Vac max), 4 channel current (1A/5A)</label>
            </p>
            <p>
              <input type="number" id="M0" name="analogBoards" min="0" max="4" defaultValue="0" />
              <label>M0) 4 channel voltage (300 Vac max), 4 channel current (1A/5A phase w/ SEF ground)</label>
            </p>
            <p>
              <input type="number" id="X6" name="analogBoards" min="0" max="4" defaultValue="0" />
              <label>X6) 7 channel current (1A/5A)</label>
            </p>
            <p>
              <input type="number" id="L2" name="analogBoards" min="0" max="4" defaultValue="0" />
              <label>L2) 7 channel current (1A/5A w/ SEF ground)</label>
            </p>
            <p>
              <input type="number" id="L6" name="analogBoards" min="0" max="4" defaultValue="0" />
              <label>L6) 4 channel current (1A/5A)</label>
            </p>
            <p>
              <input type="number" id="A9" name="analogBoards" min="0" max="4" defaultValue="0" />
              <label>A9) 4 channel current (1A/5A w/ SEF ground)</label>
            </p>
            <p>
              <input type="number" id="X9" name="analogBoards" min="0" max="4" defaultValue="0" />
              <label>X9) 4 channel voltage (300Vac max, 3 phase, 4 wire plus aux)</label>
            </p>
          </div>

          <div id="ioBoards">
            <h3>Input/Output Cards (Maximum of 6, slots 2-7)</h3>
            <p>
              <input type="number" id="W9" name="ioBoards" min="0" max="6" defaultValue="0" />
              <label>W9) 5 input, 2 output form A, 2 output form C</label>
            </p>
            <p>
              <input type="number" id="N5" name="ioBoards" min="0" max="6" defaultValue="0" />
              <label>N5) 12 input, (6) sets of 2 with shared commons</label>
            </p>
            <p>
              <input type="number" id="U4" name="ioBoards" min="0" max="6" defaultValue="0" />
              <label>U4) 7 analog inputs, (1) mVdc input (50 or 100 mV)</label>
            </p>
            <p>
              <input type="number" id="C5" name="ioBoards" min="0" max="6" defaultValue="0" />
              <label>C5) 8 outputs (5 form A, 3 form C)</label>
            </p>
            <p>
              <input type="number" id="A2" name="ioBoards" min="0" max="6" defaultValue="0" />
              <label>A2) 7 RTD, (1) mVdc input (50 or 100 mV)</label>
            </p>
          </div>

          <div id="commBoards">
            <h3>Communication Boards (Maximum of 1, slot 2 only)</h3>
            <p>
              <input type="number" id="E5" name="commBoards" min="0" max="1" defaultValue="0" />
              <label>E5) Ethernet - (1) Copper (with 3 input, 2 output form A, 1 output form C)</label>
            </p>
            <p>
              <input type="number" id="P7" name="commBoards" min="0" max="1" defaultValue="0" />
              <label>P7) Ethernet - (1) Fiber</label>
            </p> 
            <p>
              <input type="number" id="W2" name="commBoards" min="0" max="1" defaultValue="0" />
              <label>W2) Ethernet - (2) Independent Copper (with 3 input, 2 output form A, 1 output form C)</label>
            </p>
            <p>
              <input type="number" id="G3" name="commBoards" min="0" max="1" defaultValue="0" />
              <label>G3) Ethernet - (1) Copper, (1) Redundant Copper and (1) Independent Copper</label>
            </p> 
            <p>
              <input type="number" id="H8" name="commBoards" min="0" max="1" defaultValue="0" />
              <label>H8) Ethernet - (1) Fiber, (1) Redundant Fiber and (1) Independent Fiber</label>
            </p>
            <p>
              <input type="number" id="H7" name="commBoards" min="0" max="1" defaultValue="0" />
              <label>H7) Ethernet - (1) Fiber, (1) Redundant Fiber and (1) Independent Copper</label>
            </p>
          </div>

          <div id="powerSupply" className="noSpaceRight">
            <h3>Power Supply (Slot 1)</h3>
            <p>
              <input type="radio" id="PS_1" name="powerSupply" value="1" />
              <label>PS_1) 48/125 Vdc, 120 Vac</label>
            </p> 
            <p>
              <input type="radio" id="PS_2" name="powerSupply" value="2" />
              <label>PS_2) 250 Vdc, 240 Vac</label>
            </p>
            <p>
              <input type="radio" id="PS_3" name="powerSupply" value="3" />
              <label>PS_3) 24 Vdc</label>
            </p>
          </div>

          <div id="optionA" className="noSpaceRight">
            <h3>Option A</h3>
            <p>
              <input type="radio" id="N" name="optionA" value="N" defaultChecked="true" />
              <label>N) HMI Board</label>
            </p>        
          </div>

          <div id="terminalKit" className="noSpaceLeft">
            <h3>Terminal Kit</h3>
            <p>
              <input type="radio" id="0" name="terminalKit" value="0" />
              <label>0) Screw Compression</label>
            </p>         
            <p>
              <input type="radio" id="1" name="terminalKit" value="1" />
              <label>1) Spring</label>
            </p>
          </div>

          <div id="protection" className="noSpaceRight">
            <h3>Protection <FontAwesomeIcon icon={['fal', 'info-circle']} onClick={() => modalToggler("protection")}/></h3>
            <p>
              <input type="radio" id="A" name="protection" value="A" />
              <label>A) None</label>
            </p>
            <p>
              <input type="radio" id="B" name="protection" value="B" />
              <label>B) Basic Current Only</label>
            </p>
            <p>
              <input type="radio" id="C" name="protection" value="C" />
              <label>C) Basic Voltage Only</label>
            </p>
            <p>
              <input type="radio" id="D" name="protection" value="D" />
              <label>D) Intermediate</label>
            </p>
            <p>
              <input type="radio" id="E" name="protection" value="E" />
              <label>E) Enhanced</label>
            </p>
          </div>

          <div id="protocol" className="noSpaceLeft">
            <h3>Protocol <FontAwesomeIcon icon={['fal', 'info-circle']} onClick={() => modalToggler("protocol")}/></h3>
            <p>
              <input type="radio" id="00" name="protocol" value="00" />
              <label>00) Standard</label>
            </p>
            <p>
              <input type="radio" id="01" name="protocol" value="01" />
              <label>01) Package 1</label>
            </p>
            <p>
              <input type="radio" id="02" name="protocol" value="02" />
              <label>02) Package 2</label>
            </p>
            {/* <p>
              <input type="radio" id="02" name="protocol" value="02" />
              <label>02) Package 2</label>
            </p>   */}
          </div>

          <div id="optionB" className="noSpaceLeft">
            <h3>Option B</h3>
            <p>
              <input type="radio" id="N" name="optionB" value="N" defaultChecked="true" />
              <label>N) None</label>
            </p> 
          </div>
        </div>
      </div>
    </div>
  );
}

export default Configurator;